import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Col,
  Button,
  Input,
  Label,
  FormFeedback,
  TabContent,
  TabPane,
  Form,
  Nav,
  NavItem,
  NavLink,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Dropzone from "react-dropzone";
import Select from "react-select";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ProductVariantManager from "./ProductVariantManager";
import { resolveProductImageSrc } from "../../../helpers/productImageUrl";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  addNewProduct,
  updateProduct,
  getEcommerceCategories,
  getSubcategories,
  getCommissions,
  getTaxes,
} from "../../../slices/thunks";
import { getUserRole } from "../../../helpers/role_helper";
import { getLoggedinUser } from "../../../api";

const EcommerceAddProduct = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  const editId = params._id || params.id || location.state?.product?.id || location.state?.product?._id;
  const isEdit = Boolean(editId);

  document.title = isEdit ? "Edit Product - Dashboard" : "Add Product - Dashboard";

  // Get user info for seller_id
  const userProfile = getLoggedinUser();
  const userRole = getUserRole(userProfile);
  const userId = userProfile?.id || userProfile?.uid || userProfile?._id;

  // Get categories and subcategories from Redux
  const categoriesRaw = useSelector((state) => state.Ecommerce?.categories);
  const categories = Array.isArray(categoriesRaw) ? categoriesRaw : [];

  const subcategoriesRaw = useSelector((state) => state.Ecommerce?.subcategories);
  const subcategories = Array.isArray(subcategoriesRaw) ? subcategoriesRaw : [];

  const productsRaw = useSelector((state) => state.Ecommerce?.products);
  const products = Array.isArray(productsRaw) ? productsRaw : [];

  // Get commissions from Redux (only active ones)
  const commissionsRaw = useSelector((state) => state.Ecommerce?.commissions);
  const allCommissions = Array.isArray(commissionsRaw) ? commissionsRaw : [];
  const commissionType = allCommissions
    .filter((comm) => comm.status === "Active")
    .map((comm) => {
      const displayValue = comm.value.includes("%") ? comm.value : `${comm.value}%`;
      return { label: displayValue, value: comm.value };
    });

  // Get taxes from Redux (only active ones)
  const taxesRaw = useSelector((state) => state.Ecommerce?.taxes);
  const allTaxes = Array.isArray(taxesRaw) ? taxesRaw : [];
  const taxType = allTaxes
    .filter((tax) => tax.status === "Active")
    .map((tax) => {
      const displayValue = tax.value.includes("%") ? tax.value : `${tax.value}%`;
      return { label: displayValue, value: tax.value };
    });

  // State
  const [customActiveTab, setcustomActiveTab] = useState("1");
  const [selectedFiles, setselectedFiles] = useState([]);
  const [selectedVisibility, setselectedVisibility] = useState(null);
  const [productImage, setProductImage] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [productData, setProductData] = useState(null);

  // Product Variants State
  const [hasVariants, setHasVariants] = useState(false);
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);

  const productVisibility = [
    { label: "Hidden", value: "Hidden" },
    { label: "Public", value: "Public" },
    { label: "Block", value: "Block" },
    { label: "Banned", value: "Banned" },
  ];

  function handleAcceptedFiles(files) {
    const filesWithPreview = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setselectedFiles((prevFiles) => [...prevFiles, ...filesWithPreview]);
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  // Fetch categories, subcategories, and commissions on mount
  useEffect(() => {
    const fetchParams = {};
    if (userRole === "seller" && userId) {
      fetchParams.seller_id = userId;
    }
    dispatch(getEcommerceCategories(fetchParams));
    dispatch(getSubcategories(fetchParams));
    dispatch(getCommissions());
    dispatch(getTaxes());
  }, [dispatch, userRole, userId]);

  // Load existing product data if in edit mode
  useEffect(() => {
    if (isEdit) {
      let prod = location.state?.product;
      if (!prod && products.length > 0) {
        prod = products.find((p) => String(p.id || p._id) === String(editId));
      }
      if (prod) {
        setProductData(prod);
        setExistingImage(prod.thumbnail || prod.image || null);

        // Load existing gallery images if any
        if (prod.images) {
          try {
            const existingImgs = typeof prod.images === "string" ? JSON.parse(prod.images) : prod.images;
            if (Array.isArray(existingImgs)) {
              const previewFiles = existingImgs.map((filename) => ({
                name: filename,
                preview: resolveProductImageSrc(filename) || "",
                formattedSize: "Existing",
              }));
              setselectedFiles(previewFiles);
            }
          } catch (e) {
            console.error("Error parsing existing gallery images:", e);
          }
        }

        // Parse attributes and variants
        let attributesObj = {};
        if (prod.attributes) {
          try {
            attributesObj = typeof prod.attributes === "string" ? JSON.parse(prod.attributes) : prod.attributes;
          } catch (e) {
            attributesObj = {};
          }
        }

        const prodVariants = prod.variants || attributesObj.variants || [];
        const prodOptions = prod.options || attributesObj.options || [];
        const prodHasVariants = prod.has_variants === 1 || prod.has_variants === true || attributesObj.has_variants === true || (Array.isArray(prodVariants) && prodVariants.length > 0);

        setHasVariants(prodHasVariants);
        setOptions(Array.isArray(prodOptions) ? prodOptions : []);
        setVariants(Array.isArray(prodVariants) ? prodVariants : []);

        if (prod.visibility) {
          setselectedVisibility({ label: prod.visibility, value: prod.visibility });
        }
      }
    }
  }, [isEdit, editId, location.state, products]);

  // Validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: productData?.name || "",
      category_id: productData?.category_id || productData?.categoryId || "",
      subcategory_id: productData?.subcategory_id || productData?.subcategoryId || "",
      price: productData?.price != null ? String(productData.price) : "",
      purchase_price: productData?.cost_per_item != null ? String(productData.cost_per_item) : (productData?.purchase_price != null ? String(productData.purchase_price) : ""),
      stock: productData?.sku || productData?.stock || "",
      stock_quantity: productData?.stock_quantity ?? productData?.stock ?? 0,
      product_discount: productData?.product_discount || "",
      commission: productData?.commission || "",
      tax: productData?.tax || "",
      description: productData?.description || "",
      short_description: productData?.short_description || "",
      meta_title: productData?.meta_title || "",
      meta_keyword: productData?.meta_keyword || productData?.meta_keywords || "",
      meta_description: productData?.meta_description || "",
      product_tags: productData?.product_tags || productData?.tags || "",
      status: productData?.status || "draft",
      visibility: productData?.visibility || "Public",
      color: productData?.attributes?.color ? (Array.isArray(productData.attributes.color) ? productData.attributes.color.join(", ") : productData.attributes.color) : "",
      size: productData?.attributes?.size ? (Array.isArray(productData.attributes.size) ? productData.attributes.size.join(", ") : productData.attributes.size) : "",
      material: productData?.attributes?.material || "",
      weight: productData?.weight || productData?.attributes?.weight || "",
      dimensions_length: productData?.attributes?.dimensions?.length || "",
      dimensions_width: productData?.attributes?.dimensions?.width || "",
      dimensions_height: productData?.attributes?.dimensions?.height || "",
      features: productData?.attributes?.features ? (Array.isArray(productData.attributes.features) ? productData.attributes.features.join("\n") : productData.attributes.features) : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Product name is required"),
      category_id: Yup.string().required("Category is required"),
      subcategory_id: Yup.string().required("Subcategory is required"),
      price: Yup.number()
        .nullable()
        .transform((v, o) => (o === "" || o === null || o === undefined ? null : v))
        .test("price-check", "Price is required and must be positive", function (val) {
          if (hasVariants) return true; // Handled per-variant
          return val != null && val >= 0;
        }),
      purchase_price: Yup.number()
        .nullable()
        .min(0, "Purchase Price must be positive")
        .transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      stock: Yup.string().test("sku-check", "SKU is required", function (val) {
        if (hasVariants) return true; // Handled per-variant
        return !!val && val.trim().length > 0;
      }),
      stock_quantity: Yup.number()
        .min(0, "Stock QTY must be 0 or more")
        .nullable()
        .transform((v, o) => (o === "" || o === null || o === undefined ? 0 : v)),
      product_discount: Yup.string(),
      commission: Yup.string(),
      tax: Yup.string(),
      description: Yup.string(),
      short_description: Yup.string(),
      meta_title: Yup.string(),
      meta_keyword: Yup.string(),
      meta_description: Yup.string(),
      product_tags: Yup.string(),
      status: Yup.string().oneOf(["published", "draft", "scheduled"], "Invalid status"),
      visibility: Yup.string(),
      color: Yup.string(),
      size: Yup.string(),
      material: Yup.string(),
      weight: Yup.number().nullable().min(0, "Weight must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      dimensions_length: Yup.number().nullable().min(0, "Length must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      dimensions_width: Yup.number().nullable().min(0, "Width must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      dimensions_height: Yup.number().nullable().min(0, "Height must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      features: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        // Validate variants if variable product is enabled
        if (hasVariants) {
          if (!variants || variants.length === 0) {
            toast.error("Please generate or add at least one variant before submitting.");
            return;
          }
          const activeVariants = variants.filter((v) => v.status !== "disabled");
          if (activeVariants.length === 0) {
            toast.error("Please enable at least one active variant.");
            return;
          }
          const invalidPrice = activeVariants.find(
            (v) => v.price === "" || v.price === null || isNaN(parseFloat(v.price)) || parseFloat(v.price) < 0
          );
          if (invalidPrice) {
            toast.error("Please provide a valid price for all active variants.");
            return;
          }
        }

        // Create FormData to handle file upload
        const formData = new FormData();

        // Calculate dynamic price and stock from variants if enabled
        let finalPrice = parseFloat(values.price) || 0;
        let finalStockQty = parseInt(values.stock_quantity, 10) || 0;
        let finalSku = values.stock;

        if (hasVariants && variants.length > 0) {
          const activeVars = variants.filter((v) => v.status !== "disabled");
          const prices = (activeVars.length > 0 ? activeVars : variants)
            .map((v) => parseFloat(v.price))
            .filter((p) => !isNaN(p) && p > 0);
          if (prices.length > 0) {
            finalPrice = Math.min(...prices);
          }
          finalStockQty = variants.reduce(
            (sum, v) => sum + (parseInt(v.stock_quantity, 10) || 0),
            0
          );
          if (!finalSku && variants[0]?.sku) {
            finalSku = variants[0].sku;
          }
        }

        // Append all product data
        formData.append("name", values.name);
        formData.append("category_id", values.category_id);
        formData.append("subcategory_id", values.subcategory_id);
        formData.append("price", String(finalPrice));
        if (values.purchase_price) {
          formData.append("purchase_price", parseFloat(values.purchase_price));
        } else {
          formData.append("purchase_price", "");
        }
        formData.append("stock", finalSku || "");
        formData.append("stock_quantity", String(finalStockQty));
        formData.append("product_discount", values.product_discount || "");
        formData.append("commission", values.commission || "");
        formData.append("tax", values.tax || "");
        formData.append("description", values.description || "");
        formData.append("short_description", values.short_description || "");
        formData.append("meta_title", values.meta_title || "");
        formData.append("meta_keyword", values.meta_keyword || "");
        formData.append("meta_description", values.meta_description || "");
        formData.append("product_tags", values.product_tags || "");
        formData.append("status", values.status);
        formData.append("visibility", values.visibility);
        formData.append("color", values.color || "");
        formData.append("size", values.size || "");
        formData.append("material", values.material || "");
        formData.append("weight", values.weight || "");
        const dimensions = {};
        if (values.dimensions_length) dimensions.length = values.dimensions_length;
        if (values.dimensions_width) dimensions.width = values.dimensions_width;
        if (values.dimensions_height) dimensions.height = values.dimensions_height;
        formData.append("dimensions", JSON.stringify(Object.keys(dimensions).length > 0 ? dimensions : null));
        formData.append("features", values.features || "");

        // Variant & Option attributes
        formData.append("has_variants", hasVariants ? "1" : "0");
        formData.append("options", JSON.stringify(hasVariants ? options : []));
        formData.append("variants", JSON.stringify(hasVariants ? variants : []));

        // seller_id: NULL for admin products, user ID for seller products
        if (userRole === "seller" && userId) {
          formData.append("seller_id", userId);
        } else {
          formData.append("seller_id", "");
        }
        if (userId) {
          formData.append("created_by", userId);
        }

        // Append main product image
        if (productImage) {
          formData.append("productImage", productImage);
        } else if (isEdit) {
          formData.append("image", existingImage || "");
        }

        // Append gallery images
        let hasNewGalleryImages = false;
        if (selectedFiles && selectedFiles.length > 0) {
          selectedFiles.forEach((file) => {
            if (file instanceof File) {
              formData.append("galleryImages", file);
              hasNewGalleryImages = true;
            }
          });
        }

        // If editing, preserve only the retained existing gallery images (not removed)
        if (isEdit) {
          const retainedExistingImages = (selectedFiles || [])
            .filter((f) => !(f instanceof File) && f.name)
            .map((f) => f.name);
          formData.append("existingGalleryImages", JSON.stringify(retainedExistingImages));
        }

        if (isEdit) {
          formData.append("_id", String(editId));
          const productPayload = {
            formData: formData,
            _id: editId,
          };
          await dispatch(updateProduct(productPayload)).unwrap();
          toast.success("Product updated successfully!");
        } else {
          const productPayload = {
            formData: formData,
          };
          await dispatch(addNewProduct(productPayload)).unwrap();
          toast.success("Product added successfully!");
        }

        // Navigate back to products list
        navigate(userRole === "seller" ? "/seller/products" : "/products");
      } catch (error) {
        console.error("Error saving product:", error);
      }
    },
  });

  // Filter subcategories based on selected category
  const filteredSubcategories = useMemo(() => {
    const selectedCategoryId = validation.values.category_id;
    if (!selectedCategoryId) return [];

    return subcategories.filter((subcat) => {
      const subcatCategoryId = String(subcat.category_id || subcat.categoryId || "");
      const selectedId = String(selectedCategoryId);
      return subcatCategoryId === selectedId;
    });
  }, [subcategories, validation.values.category_id]);

  // Reset subcategory when category changes
  useEffect(() => {
    if (validation.values.category_id) {
      validation.setFieldValue("subcategory_id", "");
    }
  }, [validation.values.category_id]);

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <Container fluid>
        <BreadCrumb title="Add Product" pageTitle="Dashboard" />

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            <Col lg={8}>
              {/* Product Title */}
              <Card className="mb-3">
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="product-title-input">
                      Product Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      id="product-title-input"
                      placeholder="Enter product title"
                      name="name"
                      value={validation.values.name || ""}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      invalid={validation.errors.name && validation.touched.name ? true : false}
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                    ) : null}
                  </div>
                </CardBody>
              </Card>

              {/* Product Description */}
              <Card className="mb-3">
                <CardBody>
                  <div>
                    <Label>Product Description</Label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={validation.values.description || ""}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        validation.setFieldValue("description", data);
                      }}
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Product Gallery */}
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Product Gallery</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-4">
                    <h5 className="fs-14 mb-1">
                      Product Image <span className="text-danger">*</span>
                    </h5>
                    <p className="text-muted mb-3">Upload the main product image (JPG, PNG, or GIF - Max 5MB)</p>
                    <div className="d-flex flex-column align-items-center">
                      <div className="position-relative mb-3">
                        <div
                          className="bg-light rounded border d-flex align-items-center justify-content-center"
                          style={{ width: "200px", height: "200px", overflow: "hidden" }}
                        >
                          {productImage || existingImage ? (
                            <div className="position-relative w-100 h-100">
                              <img
                                src={productImage ? URL.createObjectURL(productImage) : (resolveProductImageSrc(existingImage) || "")}
                                id="product-img"
                                alt="Product"
                                className="img-fluid"
                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle shadow"
                                style={{ width: "30px", height: "30px", padding: 0, zIndex: 10 }}
                                onClick={() => {
                                  setProductImage(null);
                                  setExistingImage(null);
                                  const input = document.getElementById("product-image-input");
                                  if (input) input.value = "";
                                  toast.success("Main product image removed");
                                }}
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="text-center p-3">
                              <i className="ri-image-add-line display-4 text-muted"></i>
                              <p className="text-muted mt-2 mb-0" style={{ fontSize: "14px" }}>
                                No image selected
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-2">
                        <label htmlFor="product-image-input" className="btn btn-primary" style={{ cursor: "pointer" }}>
                          <i className="ri-upload-2-line me-1"></i>
                          {productImage ? "Change Image" : "Choose Image"}
                        </label>
                        <input
                          className="form-control d-none"
                          id="product-image-input"
                          type="file"
                          accept="image/png, image/gif, image/jpeg, image/jpg"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error("Image size must be less than 5MB");
                                return;
                              }
                              if (!file.type.match(/^image\/(png|jpeg|jpg|gif)$/)) {
                                toast.error("Please select a valid image file (PNG, JPG, or GIF)");
                                return;
                              }
                              setProductImage(file);
                              setExistingImage(null);
                              toast.success("Image selected successfully");
                            }
                          }}
                        />
                      </div>
                      {productImage && (
                        <p className="text-muted mb-0 text-center">
                          <small>
                            Selected: {productImage.name} ({(productImage.size / 1024).toFixed(2)} KB)
                          </small>
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <h5 className="fs-14 mb-1">Product Gallery</h5>
                    <p className="text-muted mb-3">
                      Upload multiple images for the product gallery (JPG, PNG, or GIF - Max 5MB each)
                    </p>
                    <Dropzone
                      onDrop={(acceptedFiles, rejectedFiles) => {
                        if (rejectedFiles && rejectedFiles.length > 0) {
                          rejectedFiles.forEach(({ file, errors }) => {
                            errors.forEach((error) => {
                              if (error.code === "file-too-large") {
                                toast.error(`${file.name} is too large. Maximum size is 5MB.`);
                              } else if (error.code === "file-invalid-type") {
                                toast.error(`${file.name} is not a valid image file.`);
                              } else {
                                toast.error(`${file.name}: ${error.message}`);
                              }
                            });
                          });
                        }
                        if (acceptedFiles && acceptedFiles.length > 0) {
                          handleAcceptedFiles(acceptedFiles);
                          toast.success(`${acceptedFiles.length} image(s) added to gallery`);
                        }
                      }}
                      accept={{
                        "image/*": [".png", ".jpg", ".jpeg", ".gif"],
                      }}
                      maxSize={5 * 1024 * 1024}
                      multiple={true}
                    >
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <div
                          className={`dropzone dz-clickable border rounded p-4 ${isDragActive ? "border-primary bg-light" : ""}`}
                          style={{
                            borderStyle: "dashed",
                            transition: "all 0.3s ease",
                            cursor: "pointer",
                          }}
                        >
                          <div className="dz-message needsclick text-center" {...getRootProps()}>
                            <input {...getInputProps()} />
                            <div className="mb-3">
                              <i
                                className={`display-4 ${isDragActive ? "text-primary" : "text-muted"} ri-upload-cloud-2-fill`}
                              />
                            </div>
                            <h5 className={isDragActive ? "text-primary" : ""}>
                              {isDragActive ? "Drop images here" : "Drop images here or click to upload"}
                            </h5>
                            <p className="text-muted mb-0">
                              <small>You can upload multiple images at once</small>
                            </p>
                          </div>
                        </div>
                      )}
                    </Dropzone>
                    {selectedFiles.length > 0 && (
                      <div className="mt-3">
                        <h6 className="mb-2">Gallery Images ({selectedFiles.length})</h6>
                        <Row className="g-2">
                          {selectedFiles.map((f, i) => {
                            return (
                              <Col key={i + "-file"} xs={6} sm={4} md={3} lg={2}>
                                <Card className="mb-2 shadow-sm border position-relative">
                                  <div className="position-relative">
                                    <img
                                      src={f.preview}
                                      alt={f.name}
                                      className="img-fluid rounded-top"
                                      style={{ width: "100%", height: "150px", objectFit: "cover" }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle"
                                      style={{ width: "28px", height: "28px", padding: 0, fontSize: "14px" }}
                                      onClick={() => {
                                        const newFiles = selectedFiles.filter((_, idx) => idx !== i);
                                        setselectedFiles(newFiles);
                                        toast.success("Image removed from gallery");
                                      }}
                                    >
                                      <i className="ri-close-line"></i>
                                    </button>
                                  </div>
                                  <CardBody className="p-2">
                                    <p className="text-truncate mb-0" style={{ fontSize: "12px" }} title={f.name}>
                                      {f.name}
                                    </p>
                                    <small className="text-muted">{f.formattedSize}</small>
                                  </CardBody>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* General Info / Meta Data Tabs */}
              <Card className="mb-3">
                <CardHeader>
                  <Nav className="nav-tabs-custom card-header-tabs border-bottom-0">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "1",
                        })}
                        onClick={() => {
                          toggleCustom("1");
                        }}
                      >
                        General Info
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "2",
                        })}
                        onClick={() => {
                          toggleCustom("2");
                        }}
                      >
                        Meta Data
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>

                <CardBody>
                  <TabContent activeTab={customActiveTab}>
                    <TabPane id="addproduct-general-info" tabId="1">
                      {/* Product Options & Variants Component */}
                      <ProductVariantManager
                        hasVariants={hasVariants}
                        onHasVariantsChange={(val) => {
                          setHasVariants(val);
                          if (!val) {
                            setVariants([]);
                          }
                        }}
                        options={options}
                        onOptionsChange={setOptions}
                        variants={variants}
                        onVariantsChange={setVariants}
                        basePrice={validation.values.price}
                        baseSku={validation.values.stock}
                        baseStock={validation.values.stock_quantity}
                        currencySymbol="£"
                      />

                      {hasVariants ? (
                        <Alert color="info" className="mb-3 d-flex align-items-center gap-2">
                          <i className="ri-information-line fs-18"></i>
                          <div>
                            <strong>Variable Product Pricing Active:</strong> Individual variant prices, SKUs, and stock quantities are configured in the table above. You can configure global discount, commission, and taxes below.
                          </div>
                        </Alert>
                      ) : (
                        <Row>
                          <Col sm={3}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-stock-input">
                                SKU <span className="text-danger">*</span>
                              </label>
                              <Input
                                type="text"
                                className="form-control"
                                id="product-stock-input"
                                placeholder="Enter SKU"
                                name="stock"
                                value={validation.values.stock || ""}
                                onBlur={validation.handleBlur}
                                onChange={validation.handleChange}
                                invalid={validation.errors.stock && validation.touched.stock ? true : false}
                              />
                              {validation.errors.stock && validation.touched.stock ? (
                                <FormFeedback type="invalid">{validation.errors.stock}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col sm={3}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-stock-qty-input">
                                Stock QTY <span className="text-danger">*</span>
                              </label>
                              <Input
                                type="number"
                                min="0"
                                className="form-control"
                                id="product-stock-qty-input"
                                placeholder="0"
                                name="stock_quantity"
                                value={validation.values.stock_quantity ?? ""}
                                onBlur={validation.handleBlur}
                                onChange={validation.handleChange}
                                invalid={validation.errors.stock_quantity && validation.touched.stock_quantity ? true : false}
                              />
                              {validation.errors.stock_quantity && validation.touched.stock_quantity ? (
                                <FormFeedback type="invalid">{validation.errors.stock_quantity}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col sm={3}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-price-input">
                                Price <span className="text-danger">*</span>
                              </label>
                              <div className="input-group mb-3">
                                <span className="input-group-text" id="product-price-addon">
                                  £
                                </span>
                                <Input
                                  type="text"
                                  className="form-control"
                                  id="product-price-input"
                                  placeholder="Enter price"
                                  name="price"
                                  aria-label="Price"
                                  aria-describedby="product-price-addon"
                                  value={validation.values.price || ""}
                                  onBlur={validation.handleBlur}
                                  onChange={validation.handleChange}
                                  invalid={validation.errors.price && validation.touched.price ? true : false}
                                />
                                {validation.errors.price && validation.touched.price ? (
                                  <FormFeedback type="invalid">{validation.errors.price}</FormFeedback>
                                ) : null}
                              </div>
                            </div>
                          </Col>

                          <Col sm={3}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-purchase-price-input">
                                Purchase Price
                              </label>
                              <div className="input-group mb-3">
                                <span className="input-group-text" id="product-purchase-price-addon">
                                  £
                                </span>
                                <Input
                                  type="text"
                                  className="form-control"
                                  id="product-purchase-price-input"
                                  placeholder="Enter purchase price"
                                  name="purchase_price"
                                  aria-label="Purchase Price"
                                  aria-describedby="product-purchase-price-addon"
                                  value={validation.values.purchase_price || ""}
                                  onBlur={validation.handleBlur}
                                  onChange={validation.handleChange}
                                  invalid={validation.errors.purchase_price && validation.touched.purchase_price ? true : false}
                                />
                                {validation.errors.purchase_price && validation.touched.purchase_price ? (
                                  <FormFeedback type="invalid">{validation.errors.purchase_price}</FormFeedback>
                                ) : null}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}

                      <Row>
                        <Col sm={4}>
                          <div className="mb-3">
                            <label className="form-label" htmlFor="product-Discount-input">
                              Discount
                            </label>
                            <div className="input-group mb-3">
                              <span className="input-group-text" id="product-Discount-addon">
                                %
                              </span>
                              <Input
                                type="text"
                                className="form-control"
                                id="product-Discount-input"
                                placeholder="Enter Discount"
                                name="product_discount"
                                aria-label="product_discount"
                                aria-describedby="product-orders-addon"
                                value={validation.values.product_discount || ""}
                                onBlur={validation.handleBlur}
                                onChange={validation.handleChange}
                              />
                            </div>
                          </div>
                        </Col>

                        <Col sm={3}>
                          <div className="mb-3">
                            <label className="form-label" htmlFor="product-commission-input">
                              Commission
                            </label>
                            <Input
                              name="commission"
                              type="select"
                              className="form-select"
                              id="commission-field"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.commission || ""}
                            >
                              <option value="">Select Commission</option>
                              {commissionType.map((item, key) => (
                                <option value={item.value} key={key}>
                                  {item.label}
                                </option>
                              ))}
                            </Input>
                          </div>
                        </Col>

                        <Col sm={3}>
                          <div className="mb-3">
                            <label className="form-label" htmlFor="product-tax-input">
                              Tax
                            </label>
                            <Input
                              name="tax"
                              type="select"
                              className="form-select"
                              id="tax-field"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.tax || ""}
                            >
                              <option value="">Select Tax</option>
                              {taxType.map((item, key) => (
                                <option value={item.value} key={key}>
                                  {item.label}
                                </option>
                              ))}
                            </Input>
                          </div>
                        </Col>
                      </Row>

                      {/* Product Attributes Section */}
                      <div className="mt-4">
                        <h5 className="fs-16 mb-3">Product Attributes</h5>
                        <Row>
                          <Col sm={6}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-material-input">
                                Material
                              </label>
                              <Input
                                type="text"
                                className="form-control"
                                id="product-material-input"
                                placeholder="e.g., Cotton, Polyester, Leather"
                                name="material"
                                value={validation.values.material || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                            </div>
                          </Col>
                          <Col sm={6}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-weight-input">
                                Weight (kg)
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                className="form-control"
                                id="product-weight-input"
                                placeholder="0.00"
                                name="weight"
                                value={validation.values.weight || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={validation.errors.weight && validation.touched.weight ? true : false}
                              />
                              {validation.errors.weight && validation.touched.weight ? (
                                <FormFeedback type="invalid">{validation.errors.weight}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={4}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-dimension-length-input">
                                Length (cm)
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                className="form-control"
                                id="product-dimension-length-input"
                                placeholder="0.00"
                                name="dimensions_length"
                                value={validation.values.dimensions_length || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                            </div>
                          </Col>
                          <Col sm={4}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-dimension-width-input">
                                Width (cm)
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                className="form-control"
                                id="product-dimension-width-input"
                                placeholder="0.00"
                                name="dimensions_width"
                                value={validation.values.dimensions_width || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                            </div>
                          </Col>
                          <Col sm={4}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-dimension-height-input">
                                Height (cm)
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                className="form-control"
                                id="product-dimension-height-input"
                                placeholder="0.00"
                                name="dimensions_height"
                                value={validation.values.dimensions_height || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={12}>
                            <div className="mb-3">
                              <label className="form-label" htmlFor="product-features-input">
                                Features (one per line or comma-separated)
                              </label>
                              <textarea
                                className="form-control"
                                id="product-features-input"
                                placeholder="e.g., Machine Washable, Waterproof, 100% Organic"
                                name="features"
                                rows="4"
                                value={validation.values.features || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                              <small className="text-muted">Enter features, each on a new line or separated by commas</small>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </TabPane>

                    <TabPane id="addproduct-metadata" tabId="2">
                      <Row>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label" htmlFor="meta-title-input">
                              Meta title
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              placeholder="Enter meta title"
                              id="meta-title-input"
                              name="meta_title"
                              value={validation.values.meta_title || ""}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                            />
                          </div>
                        </Col>

                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label" htmlFor="meta-keywords-input">
                              Meta Keywords
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              placeholder="Enter meta keywords"
                              id="meta-keywords-input"
                              name="meta_keyword"
                              value={validation.values.meta_keyword || ""}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                            />
                          </div>
                        </Col>
                      </Row>

                      <div>
                        <Label className="form-label" htmlFor="meta-description-input">
                          Meta Description
                        </Label>
                        <textarea
                          className="form-control"
                          id="meta-description-input"
                          placeholder="Enter meta description"
                          name="meta_description"
                          rows="3"
                          value={validation.values.meta_description || ""}
                          onChange={validation.handleChange}
                        ></textarea>
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>

              <div className="text-end mb-3">
                <Button
                  type="button"
                  color="light"
                  onClick={() => navigate(userRole === "seller" ? "/seller/products" : "/products")}
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button type="submit" color="success">
                  Submit
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              {/* Publish */}
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Publish</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label htmlFor="choices-publish-status-input" className="form-label">
                      Status <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="status"
                      type="select"
                      className="form-select"
                      id="choices-publish-status-input"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.status || ""}
                      invalid={validation.touched.status && validation.errors.status ? true : false}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="scheduled">Scheduled</option>
                    </Input>
                    {validation.touched.status && validation.errors.status ? (
                      <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
                    ) : null}
                  </div>

                  <div>
                    <Label htmlFor="choices-publish-visibility-input" className="form-label">
                      Visibility
                    </Label>
                    <Select
                      value={selectedVisibility}
                      onChange={(selected) => {
                        setselectedVisibility(selected);
                        validation.setFieldValue("visibility", selected?.value || "");
                      }}
                      options={productVisibility}
                      name="choices-publish-visibility-input"
                      classNamePrefix="select2-selection form-select"
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Product Categories */}
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Product Categories</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-2">Select Category</p>
                  <Input
                    name="category_id"
                    type="select"
                    className="form-select"
                    id="category-field-2"
                    onChange={(e) => {
                      validation.handleChange(e);
                      validation.setFieldValue("subcategory_id", "");
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.category_id || ""}
                    invalid={validation.touched.category_id && validation.errors.category_id ? true : false}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id || category._id} value={category.id || category._id}>
                        {category.name}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.category_id && validation.errors.category_id ? (
                    <FormFeedback type="invalid">{validation.errors.category_id}</FormFeedback>
                  ) : null}
                  <div className="mt-3">
                    <p className="text-muted mb-2">Select Sub Category</p>
                    <Input
                      name="subcategory_id"
                      type="select"
                      className="form-select"
                      id="subcategory-field"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.subcategory_id || ""}
                      disabled={!validation.values.category_id}
                      invalid={validation.touched.subcategory_id && validation.errors.subcategory_id ? true : false}
                    >
                      <option value="">Select Subcategory</option>
                      {filteredSubcategories.length > 0 ? (
                        filteredSubcategories.map((subcategory) => (
                          <option key={subcategory.id || subcategory._id} value={subcategory.id || subcategory._id}>
                            {subcategory.name}
                          </option>
                        ))
                      ) : validation.values.category_id ? (
                        <option value="" disabled>
                          No subcategories found for this category
                        </option>
                      ) : null}
                    </Input>
                    {validation.touched.subcategory_id && validation.errors.subcategory_id ? (
                      <FormFeedback type="invalid">{validation.errors.subcategory_id}</FormFeedback>
                    ) : null}
                  </div>
                </CardBody>
              </Card>

              {/* Product Tags */}
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Product Tags</h5>
                </CardHeader>
                <CardBody>
                  <div className="hstack gap-3 align-items-start">
                    <div className="flex-grow-1">
                      <Input
                        className="form-control"
                        placeholder="Enter tags"
                        type="text"
                        name="product_tags"
                        value={validation.values.product_tags || ""}
                        onBlur={validation.handleBlur}
                        onChange={validation.handleChange}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Product Short Description */}
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Product Short Description</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-2">Add short description for product</p>
                  <textarea
                    className="form-control"
                    placeholder="Must enter minimum of a 100 characters"
                    rows="3"
                    name="short_description"
                    value={validation.values.short_description || ""}
                    onChange={validation.handleChange}
                  ></textarea>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default EcommerceAddProduct;
